/**
 * Commands from the host page to the widget: `PweChat.open()`, `close()` and
 * `toggle()` (embed.ts), or `openChat()` and friends for a React host
 * (index.ts).
 *
 * A module-level channel rather than a `window` event: nothing else on a
 * client's page can fire it by accident, and there is no event name to agree
 * on. A command sent before the widget is listening — `PweChat.open()` straight
 * after `init()`, while the configuration is still being fetched — is kept, and
 * the last one is delivered the moment the panel mounts.
 */
export type ChatCommand = 'open' | 'close' | 'toggle';

type Listener = (command: ChatCommand) => void;

const listeners = new Set<Listener>();
let pending: ChatCommand | null = null;

export function sendCommand(command: ChatCommand): void {
  if (listeners.size === 0) {
    pending = command;
    return;
  }
  listeners.forEach((listener) => listener(command));
}

/** Subscribe; returns the unsubscribe. A command sent before anyone listened is delivered now. */
export function onCommand(listener: Listener): () => void {
  listeners.add(listener);
  if (pending) {
    const command = pending;
    pending = null;
    listener(command);
  }
  return () => {
    listeners.delete(listener);
  };
}

export const openChat = (): void => sendCommand('open');
export const closeChat = (): void => sendCommand('close');
export const toggleChat = (): void => sendCommand('toggle');
